import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { type Control, type FieldValues, type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { api, type BillingProfile } from "@/api";
import { Button } from "@osmix/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@osmix/ui/input";
export type BillingProfileFormValues = {
  companyName: string;
  inn: string;
  kpp: string;
  ogrn: string;
  legalAddress: string;
  bankName: string;
  bankAccount: string;
  corrAccount: string;
  bik: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
};
const emptyValues = (): BillingProfileFormValues => ({
  companyName: "",
  inn: "",
  kpp: "",
  ogrn: "",
  legalAddress: "",
  bankName: "",
  bankAccount: "",
  corrAccount: "",
  bik: "",
  contactName: "",
  contactEmail: "",
  contactPhone: ""
});
const fromProfile = (p: BillingProfile): BillingProfileFormValues => ({
  companyName: p.companyName,
  inn: p.inn,
  kpp: p.kpp,
  ogrn: p.ogrn,
  legalAddress: p.legalAddress,
  bankName: p.bankName,
  bankAccount: p.bankAccount,
  corrAccount: p.corrAccount,
  bik: p.bik,
  contactName: p.contactName,
  contactEmail: p.contactEmail,
  contactPhone: p.contactPhone
});

// Реквизиты ЮЛ покупателя (fd-payment-02). Обязательны только название и ИНН —
// остальное опционально (как на бэкенде). Сохраняет через PUT billing-profile.
export function BillingProfileForm({
  profile,
  onSaved
}: {
  profile: BillingProfile | null;
  onSaved: () => Promise<void> | void;
}) {
  const {
    t
  } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const schema = z.object({
    companyName: z.string().min(1, t("checkout.required")),
    inn: z.string().min(1, t("checkout.required")),
    kpp: z.string(),
    ogrn: z.string(),
    legalAddress: z.string(),
    bankName: z.string(),
    bankAccount: z.string(),
    corrAccount: z.string(),
    bik: z.string(),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string()
  });
  const form = useForm<BillingProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: profile ? fromProfile(profile) : emptyValues()
  });
  useEffect(() => {
    form.reset(profile ? fromProfile(profile) : emptyValues());
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the loaded profile changes
  }, [profile]);
  const onSubmit = async (values: BillingProfileFormValues) => {
    setSubmitting(true);
    try {
      await api.buyer.updateBillingProfile(values);
      await onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };
  return <Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 rounded-2xl bg-card p-5 shadow-block" data-testid="billing-form">
				<div className="grid gap-4 sm:grid-cols-2">
					<Field control={form.control} name="companyName" label={t("billing.companyName")} className="sm:col-span-2" />
					<Field control={form.control} name="inn" label={t("billing.inn")} />
					<Field control={form.control} name="kpp" label={t("billing.kpp")} />
					<Field control={form.control} name="ogrn" label={t("billing.ogrn")} />
					<Field control={form.control} name="legalAddress" label={t("billing.legalAddress")} className="sm:col-span-2" />
					<Field control={form.control} name="bankName" label={t("billing.bankName")} className="sm:col-span-2" />
					<Field control={form.control} name="bankAccount" label={t("billing.bankAccount")} />
					<Field control={form.control} name="corrAccount" label={t("billing.corrAccount")} />
					<Field control={form.control} name="bik" label={t("billing.bik")} />
					<Field control={form.control} name="contactName" label={t("billing.contactName")} />
					<Field control={form.control} name="contactEmail" label={t("billing.contactEmail")} />
					<Field control={form.control} name="contactPhone" label={t("billing.contactPhone")} />
				</div>
				<Button type="submit" variant="osmix-filled" size="pill" className="self-start" disabled={submitting} data-testid="billing-save">
					{submitting ? t("checkout.processing") : t("billing.save")}
				</Button>
			</form>
		</Form>;
}
function Field(t0) {
  const $ = _c(7);
  const {
    control,
    name,
    label,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== label) {
    t1 = t2 => {
      const {
        field
      } = t2;
      return <FormItem className={className}><FormLabel>{label}</FormLabel><FormControl><Input {...field} value={String(field.value ?? "")} /></FormControl><FormMessage /></FormItem>;
    };
    $[0] = className;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== control || $[4] !== name || $[5] !== t1) {
    t2 = <FormField control={control} name={name} render={t1} />;
    $[3] = control;
    $[4] = name;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}